import { useParams } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import CommonImg from "@/components/Common/Img";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import EstimateIconRed from "@/assets/images/Tab/bid-estimate-icon-red.png";
import { useEstimateInfo } from "../hooks/useScenarioStore";
import { useReadProjectGroupsItemSheetLatest } from "@/libs/react-query/hooks/useItemsheetsQuery";
import { ItemSheetData, ItemSheetExtraData } from "@/types/itemsheet";
import { BuyerGroupingTable } from "@/types/scenario";
import { ItemSheetExtraTag } from "@/consts/Estimate";
import { calculateGroupSummary, getNextScenarioTitle } from "../utils";
import ScenarioGroupTable from "./table/ScenarioGroupTable";
import { useReadProjectGroupsScenarios } from "@/libs/react-query/hooks/useScenarioQuery";
import { useBudgetAmountMap } from "../hooks/useBudgetAmountMap";

export type CreateScenarioMode = "create" | "edit";

interface Props {
  latestSheetId: string;
  setOpen: (open: boolean) => void;
  defaultTitle?: string;
  existingScenarioData?: BuyerGroupingTable[];
  onSubmit: (data: {
    title: string;
    latestSheetId: string;
    divisions: BuyerGroupingTable[];
  }) => void;
  mode?: CreateScenarioMode;
  defaultProjectId?: string;
}

export default function CreateOrEditScenarioContents({
  latestSheetId,
  setOpen,
  defaultTitle,
  existingScenarioData,
  onSubmit,
  mode = "create",
  defaultProjectId = "",
}: Props) {
  const { group_id = "" } = useParams();
  const estimateInfo = useEstimateInfo();

  const { data: sheetData } = useReadProjectGroupsItemSheetLatest(
    group_id,
    latestSheetId,
    {
      project_id: estimateInfo.projectId,
      project_type: estimateInfo.projectType,
      original_id: estimateInfo.originalId,
    }
  );
  const { data } = useReadProjectGroupsScenarios(group_id);
  const { scenarios = [] } = data || {};

  const { budgetMap } = useBudgetAmountMap([
    defaultProjectId || estimateInfo.projectId,
  ]);

  const groupItemSheetDataByBuyerWithTags = (
    data: ItemSheetData[],
    extras: ItemSheetExtraData[]
  ): BuyerGroupingTable[] => {
    const extraMap = new Map<string, ItemSheetExtraData>();
    extras.forEach((extra) => extraMap.set(extra.data_uuid!, extra));

    const buyerSet = new Set<string>();

    data.forEach((item) => {
      if (item.buyer === "TPP") {
        buyerSet.add("TPP");
      }
    });

    data.forEach((item) => {
      const tags = extraMap.get(item.uuid!)?.tags ?? [];
      const isOwnProduct = tags.includes(ItemSheetExtraTag.OWN_PRODUCT);
      const buyer = isOwnProduct ? (item.buyer ?? "미지정") : "TPP";
      if (buyer !== "") buyerSet.add(buyer);
    });

    const groupMap: Record<string, BuyerGroupingTable> = {};
    buyerSet.forEach((buyer) => {
      groupMap[buyer] = {
        buyer,
        items: [],
        discountRate: 0,
        summary: calculateGroupSummary([], 0),
      };
    });

    let hasUnspecifiedItems = false;

    data.forEach((item) => {
      const dataId = item.uuid!;

      const extra = extraMap.get(dataId);
      const tags = extra?.tags ?? [];

      const isOwnProduct = tags.includes(ItemSheetExtraTag.OWN_PRODUCT);

      if (isOwnProduct) {
        // 자사제품일 경우, 산출내역서 에서 가져온 unit_cost는 0으로 초기화.
        item.unit_cost = 0;
        item.isCustomPrice = tags.includes(ItemSheetExtraTag.CUSTOM_PRICE);
        item.isFloorPrice = tags.includes(ItemSheetExtraTag.FLOOR_PRICE);

        const rawUnitCost = extra?.unit_cost;
        if (rawUnitCost != null) {
          const parsed = Number(rawUnitCost);

          if (!isNaN(parsed)) {
            item.unit_cost = Math.round(parsed);
          }
        }
        const quantity = item.quantity ?? 0;
        item.total_cost = item.unit_cost * quantity;
      }
      const isMissingCost = item.unit_cost == null;

      // 원래 디비전 정보 보존
      (item as any).originalBuyer = (item as any).originalBuyer || item.buyer;

      // buyer가 없거나 비어있는 경우 TPP로 기본 설정
      if (!item.buyer || item.buyer.trim() === "") {
        item.buyer = "TPP";
      }

      const buyer = isOwnProduct ? (item.buyer ?? "미지정") : "TPP";

      if (isOwnProduct && isMissingCost) {
        if (!groupMap["미지정"]) {
          groupMap["미지정"] = {
            buyer: "미지정",
            items: [],
            discountRate: 0,
            summary: calculateGroupSummary([], 0),
          };
        }
        groupMap["미지정"].items?.push(item);
        hasUnspecifiedItems = true;
      } else {
        groupMap[buyer]?.items?.push(item);
      }
    });

    const groups = Object.values(groupMap).map((group) => {
      const discountRate = group.buyer == "BID SERA" ? 5 : 0;

      return {
        ...group,
        discountRate,
        summary: calculateGroupSummary(group.items, discountRate),
      };
    });

    // Custom Price 아이템들을 별도 그룹으로 분리
    const result: BuyerGroupingTable[] = [];
    groups.forEach((group) => {
      const regularItems = group.items.filter((item) => !item.isCustomPrice);
      const customPriceItems = group.items.filter((item) => item.isCustomPrice);

      // 일반 아이템들로 기존 그룹 유지
      if (regularItems.length > 0) {
        result.push({
          ...group,
          items: regularItems,
          summary: calculateGroupSummary(regularItems, group.discountRate),
        });
      }

      // Custom Price 아이템들로 새로운 그룹 생성
      if (customPriceItems.length > 0) {
        result.push({
          buyer: `${group.buyer} C`,
          items: customPriceItems,
          discountRate: group.discountRate,
          summary: calculateGroupSummary(customPriceItems, group.discountRate),
          isCustomGroup: true,
        });
      }
    });

    return result;
  };

  const [divisions, setDivisions] = useState<BuyerGroupingTable[]>([]);

  // Custom Price 아이템들을 별도 그룹으로 분리하는 함수
  const separateCustomPriceItems = (
    groups: BuyerGroupingTable[]
  ): BuyerGroupingTable[] => {
    const result: BuyerGroupingTable[] = [];
    groups.forEach((group) => {
      // 이미 "C"로 끝나는 그룹은 이미 분리된 것이므로 그대로 추가
      if (group.buyer.endsWith(" C")) {
        result.push(group);
        return;
      }

      const regularItems = group.items.filter((item) => !item.isCustomPrice);
      const customPriceItems = group.items.filter((item) => item.isCustomPrice);

      // 일반 아이템들로 기존 그룹 유지
      if (regularItems.length > 0) {
        result.push({
          ...group,
          items: regularItems,
          summary: calculateGroupSummary(regularItems, group.discountRate),
        });
      }

      // Custom Price 아이템들로 새로운 그룹 생성
      if (customPriceItems.length > 0) {
        result.push({
          buyer: `${group.buyer} C`,
          items: customPriceItems,
          discountRate: group.discountRate,
          summary: calculateGroupSummary(customPriceItems, group.discountRate),
          isCustomGroup: true,
        });
      }
    });

    return result;
  };

  useEffect(() => {
    if (mode === "edit" && existingScenarioData) {
      if (sheetData?.latest?.data) {
        const latestData = sheetData.latest.data.data;
        const latestExtras = sheetData.latest.data.extras;
        const latestDataMap = new Map(
          latestData.map((item) => [item.uuid!, item])
        );
        const extraMap = new Map<string, ItemSheetExtraData>();
        latestExtras.forEach((extra) => extraMap.set(extra.data_uuid!, extra));

        const updatedDivisions = existingScenarioData.map((division) => ({
          ...division,
          items: division.items.map((item) => {
            const latestItem = latestDataMap.get(item.uuid!);
            if (latestItem) {
              // extras에서 최신 태그 정보 가져오기
              const extra = extraMap.get(item.uuid!);
              const tags = extra?.tags ?? [];
              const isCustomPrice = tags.includes(ItemSheetExtraTag.CUSTOM_PRICE);
              const isFloorPrice = tags.includes(ItemSheetExtraTag.FLOOR_PRICE);

              return {
                ...latestItem,
                buyer: item.buyer,
                originalBuyer: (item as any).originalBuyer,
                total_cost: item.total_cost,
                unit_cost: item.unit_cost,
                isCustomPrice,
                isFloorPrice,
              };
            }
            return item;
          }),
        }));

        const finalDivisions = updatedDivisions.map((division) => ({
          ...division,
          summary: calculateGroupSummary(division.items, division.discountRate),
        }));

        // Edit 모드에서도 Custom Price 아이템 분리 적용
        const separatedDivisions = separateCustomPriceItems(finalDivisions);
        setDivisions(separatedDivisions);
      } else {
        // sheetData가 없을 때도 분리 적용
        const separatedDivisions = separateCustomPriceItems(existingScenarioData);
        setDivisions(separatedDivisions);
      }
      return;
    }

    if (sheetData?.latest?.data) {
      const grouped = groupItemSheetDataByBuyerWithTags(
        sheetData.latest.data.data,
        sheetData.latest.data.extras
      );
      setDivisions(grouped);
    }
  }, [existingScenarioData, mode, sheetData]);

  const totalSummary = useMemo(() => {
    const bidAmount =
      divisions.reduce((acc, { summary }) => acc + summary.supplyPrice, 0) *
      1.1;
    const baseAmountMap = budgetMap[
      defaultProjectId || estimateInfo.projectId
    ] || {
      budget: 0,
      basisAmount: undefined,
    };
    const baseAmount = baseAmountMap.basisAmount
      ? Number(baseAmountMap.basisAmount)
      : baseAmountMap.budget;
    const bidRate = baseAmount > 0 ? (bidAmount / baseAmount) * 100 : 0;

    return { bidAmount, baseAmount, bidRate, baseAmountMap };
  }, [
    divisions.map((d) => d.summary.supplyPrice).join(","),
    divisions.map((d) => d.summary.totalCost).join(","),
    divisions.map((d) => d.summary.margin).join(","),
    budgetMap,
    defaultProjectId,
    estimateInfo.projectId,
  ]);

  const updateDiscountRate = (buyer: string, newRate: number) => {
    setDivisions((prev) => {
      const updated = [...prev];
      const index = updated.findIndex((g) => g.buyer === buyer);
      if (index === -1) return prev;

      const group = updated[index];
      updated[index] = {
        ...group,
        discountRate: newRate,
        summary: calculateGroupSummary(group.items, newRate),
      };
      return updated;
    });
  };

  const addDivision = (buyer: string) => {
    setDivisions((prev) => {
      const newGroup: BuyerGroupingTable = {
        buyer,
        discountRate: 0,
        items: [],
        summary: calculateGroupSummary([], 0),
        isCustomGroup: true,
      };
      const next = [...prev, newGroup];
      const ungroupedIndex = next.findIndex((g) => g.buyer === "미지정");
      if (ungroupedIndex === -1 || ungroupedIndex === next.length - 1)
        return next;
      const [ungrouped] = next.splice(ungroupedIndex, 1);
      next.push(ungrouped);
      return next;
    });
  };

  const handleSubmit = () => {
    if (!latestSheetId) return;
    const title = defaultTitle || getNextScenarioTitle(scenarios);
    // GSD C를 별도 division으로 유지
    onSubmit({ title, latestSheetId, divisions });
    setOpen(false);
  };

  return (
    <>
      <div className="w-full bg-white rounded-xl border-[1px] border-gray-D">
        <div className="flex justify-between items-center px-5 py-4">
          <div className="relative flex gap-2 items-center">
            <CommonImg src={EstimateIconRed} alt="estimate" />
            <span className="text-sm text-gray-A font-semibold">
              시나리오 {mode === "edit" ? "수정" : "작성"}
            </span>
          </div>
          <Button
            size="trigger"
            className="flex gap-2 bg-dark-red hover:bg-dark-red/90"
            onClick={() => {
              const newBuyer = prompt("추가할 디비전 명을 입력하세요");
              if (newBuyer) addDivision(newBuyer);
            }}
          >
            <Plus size={16} className="text-white" />
            <span className="text-sm font-semibold text-white">
              디비전 추가하기
            </span>
          </Button>
        </div>

        <div className="min-h-[280px] border-gray-D border-t-[1px] px-5 py-3">
          <ScenarioGroupTable
            data={divisions}
            setData={setDivisions}
            onDiscountRateChange={updateDiscountRate}
          />
        </div>

        <div className="p-5 flex text-sm font-semibold gap-0.5">
          <div className="flex gap-0.5 text-gray-B">
            투찰률
            <span className="text-dark-red">
              {totalSummary.bidRate.toFixed(2)}% ={" "}
            </span>
          </div>
          <div className="flex gap-0.5 text-gray-C">
            투찰 금액
            <span className="text-gray-A">
              {totalSummary.bidAmount.toLocaleString()}
            </span>
            ÷{" "}
            {totalSummary.baseAmountMap.basisAmount ? "기초 금액" : "배정 예산"}
          </div>
          <span className="text-gray-A">
            {(
              totalSummary.baseAmountMap.basisAmount ||
              totalSummary.baseAmountMap.budget
            ).toLocaleString()}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-4 mt-6">
        <div className="flex justify-center items-center mt-5">
          <Button
            className="w-[150px] h-10 bg-dark-brown hover:bg-dark-brown/80 text-white"
            onClick={handleSubmit}
          >
            <span className="text-white text-sm font-semibold">
              시나리오 {mode === "edit" ? "수정 완료" : "추가 완료"}
            </span>
          </Button>
        </div>
      </div>
    </>
  );
}
